import { Box, Skeleton, Grid, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useEffect, useRef, useState } from 'react';
import { Navigation, Pagination, Mousewheel } from 'swiper';
// Disabling rule due to this issue https://stackoverflow.com/questions/69202975/module-not-found-cant-resolve-swiper-react
/* eslint-disable import/no-unresolved */
import { SwiperSlide } from 'swiper/react';
import SwiperCarousel from '../shared/SwiperCarousel';
// import HandsetContext from './HandsetContext.context';
import styles from './styles';

const HandsetImageSlider = ({
  loadingHandset,
  variant,
  variantOutOfStock,
  OutOfStockImg,
  OutOfStockImgAlt,
}) => {
  // const { state } = useContext(HandsetContext);

  const swiperRef = useRef(null);
  const theme = useTheme();
  const md = useMediaQuery(theme.breakpoints.up('md'));
  const style = styles(theme);
  const [images, setImages] = useState([]);

  useEffect(() => {
    if (variantOutOfStock) {
      setImages([{ path: `${OutOfStockImg}`, altText: OutOfStockImgAlt }]);
    } else {
      setImages(variant?.images);
    }
  }, [variantOutOfStock, variant?.code]);

  const updateNav = swiper => {
    if (!swiper?.el) return;
    const prev = swiper.el.querySelector('.swiper-button-prev');
    const next = swiper.el.querySelector('.swiper-button-next');
    if (!prev || !next) return;

    const prevDisabled = swiper.isBeginning;
    prev.setAttribute('tabindex', swiper.isBeginning ? '-1' : '0');

    prev.setAttribute('aria-disabled', String(prevDisabled));
    prev.style.pointerEvents = prevDisabled ? 'none' : 'auto';
    prev.onkeydown = prevDisabled
      ? null
      : e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            swiper.slidePrev();
          }
        };

    // Do not focus next on last slide
    const nextDisabled = swiper.isEnd;
    next.setAttribute('tabindex', swiper.isEnd ? '-1' : '0');

    next.setAttribute('aria-disabled', String(nextDisabled));
    next.style.pointerEvents = nextDisabled ? 'none' : 'auto';
    next.onkeydown = nextDisabled
      ? null
      : e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            swiper.slideNext();
          }
        };
  };

  const enhancePaginationBullets = swiper => {
    const dots = swiper?.el?.querySelectorAll('.swiper-pagination-bullet');
    if (!dots) return;

    let active = 0;
    if (typeof swiper.realIndex === 'number') {
      active = swiper.realIndex;
    } else if (typeof swiper.activeIndex === 'number') {
      active = swiper.activeIndex;
    }

    dots.forEach((dot, i) => {
      dot.setAttribute('tabindex', '0');
      dot.setAttribute('role', 'button');

      let label;
      if (i === active) {
        label = 'Active handset image';
      } else if (i < active) {
        label = 'Previous handset image';
      } else {
        label = 'Next handset image';
      }

      dot.setAttribute('aria-label', label);

      if (i === active) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');

      dot.addEventListener('keydown', e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          swiper.slideTo(i);
        }
      });
    });
  };

  return (
    <Grid container justifyContent='space-around' direction='row' sx={{ height: '100%' }}>
      <Grid item xs={12} md={2} sx={{ display: { xs: 'none', md: 'block' } }}>
        <ImageSlideSelector
          loadingHandset={loadingHandset}
          swiperRef={swiperRef}
          handsetImages={images}
        />
      </Grid>
      <Grid item xs={12} md={9} sx={{ background: theme.palette.primary.contrastText }}>
        {loadingHandset ? (
          // skeleton
          <>
            <Skeleton
              variant='rectangular'
              sx={{
                width: { xs: 150, sm: '100%' },
                height: { xs: 300, sm: '100%' },
                m: '0 auto',
                borderRadius: { xs: 5, sm: 0 },
              }}
            />
            <Skeleton
              variant='text'
              sx={{ m: 2, ml: 'auto', mr: 'auto', display: { xs: 'block', sm: 'none' } }}
              width={60}
            />
          </>
        ) : (
          <SwiperCarousel
            settings={{
              className: images?.length >= 2 ? 'handset-carousel' : 'single-handset',
              ref: swiperRef,
              pagination: md ? false : { enable: true, clickable: true }, // above md, pagination is disabled
              navigation: !!md, // above md, nav arrow is enabled
              modules: [Pagination, Navigation],
              onInit: swiper => {
                const prev = swiper.el.querySelector('.swiper-button-prev');
                const next = swiper.el.querySelector('.swiper-button-next');
                prev?.setAttribute('role', 'button');
                prev?.setAttribute('aria-label', 'Previous image');
                prev?.setAttribute('tabindex', '0');
                next?.setAttribute('role', 'button');
                next?.setAttribute('aria-label', 'Next image');
                next?.setAttribute('tabindex', '0');
                swiper.update();
                setTimeout(() => {
                  updateNav(swiper);
                  enhancePaginationBullets(swiper);
                }, 100);
              },
              onSlideChange: swiper => {
                updateNav(swiper);
                enhancePaginationBullets(swiper);
              },
            }}
            style={style.imageSlider.swiper}
            isHandsetDetails
          >
            {images?.map((image, i) => (
              <SwiperSlide key={i}>
                <Box src={image.path} alt={image.altText} component='img' />
              </SwiperSlide>
            ))}
          </SwiperCarousel>
        )}
      </Grid>
    </Grid>
  );
};

const ImageSlideSelector = ({ loadingHandset, handsetImages, swiperRef }) => {
  const [active, setActive] = useState('');
  const swipeOnClick = index => {
    swiperRef?.current?.swiper?.slideTo(index);
    setActive(index);
  };
  useEffect(() => {
    setActive(0);
  }, []);
  const theme = useTheme();
  const style = styles(theme);
  return loadingHandset ? (
    // skeleton
    Array(3)
      .fill()
      .map((_, i) => (
        <Skeleton key={i} variant='rectangular' height='4rem' width='90%' sx={{ mb: 2 }} />
      ))
  ) : (
    <Box
      sx={{
        '& .swiper-slide': { overflow: 'visible !important' },
        '& .swiper-wrapper': { overflow: 'visible !important' },
        '& .swiper': { overflow: 'visible !important' },
      }}
    >
      <SwiperCarousel
        settings={{
          className: 'image-selector',
          navigation: true,
          modules: [Navigation, Mousewheel],
          direction: 'vertical',
          slidesPerView: 'auto',
          mousewheel: true,
          pagination: { enable: true, clickable: true },
          slideDuplicateActiveClass: true,
        }}
        style={style.imageSlider.swiperIndex}
      >
        {handsetImages?.map((image, index) => (
          <SwiperSlide key={index}>
            <Box
              onClick={() => swipeOnClick(index)}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  swipeOnClick(index);
                }
              }}
              id={index}
              key={index}
              src={image.path}
              alt={image.altText}
              component='img'
              role='button'
              tabIndex={0}
              aria-label={`View image ${index + 1}${image?.altText ? `: ${image.altText}` : ''}`}
              sx={{
                width: '100%',
                height: 'auto',
                cursor: 'pointer',
                padding: '0.5rem 1.2rem',
                border: [
                  active === index ? `2px solid ${theme.palette.secondary.activeSlide}` : '',
                ],
              }}
            />
          </SwiperSlide>
        ))}
      </SwiperCarousel>
    </Box>
  );
};

export default HandsetImageSlider;
